import * as React from "react"
import { Col, Row } from "react-grid-system"
import styled from "styled-components"
import UsageStatisticsSection from "../../organisms/charts/UsageStatisticsSection"
import ProgressBar from "../ProgressBar"
import { Spacer } from "../../atoms/spacers"
import { MediaQuery, TERMINAL_MAX_WARNING, getTerminalGroupsText } from "../../../utils/design-helpers"
import { getPercentage, mbGbDisplay } from "../../../utils/helpers"
import TerminalStatusCircle from "../../atoms/TerminalStatusCircle"

import { ArrowDropDownIcon } from "../../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../../atoms/icons/ArrowDropUpIcon"
import AddDataSection from "../AddDataSection"
import { TerminalDataModel } from "../../../utils/models"
import AddDataButton from "../../atoms/buttons/AddDataButton"
import BodyMedium from "../../atoms/typography/BodyMedium"
import Body from "../../atoms/typography/Body"
import Checkbox from "../../atoms/inputs/Checkbox"
import Tooltip, { TooltipContainer, TooltipItem, TooltipPosition } from "../../molecules/Tooltip"
import { theme } from "../../theme"

const noDataPlanTooltipPosition: TooltipPosition = {
  transform: "translateX(-70%)",
  left: "70%",
  paddingTop: "1rem",
}

interface Props {
  terminal: TerminalDataModel
  useGreyBackground: boolean
  onClickTerminalDetails: (id: number) => void
  onCloseCard: (id: number) => void
  cardOpen: boolean
  selectMode?: boolean
  selectedForExport?: boolean
  onSelectForExport?: (selected: boolean) => void
}

const BarContentContainer = styled.div<{ greybg?: boolean }>`
  background-color: ${(props) => (props.greybg ? props.theme.colors.greyLighter : props.theme.colors.white)};
  padding: ${(props) => props.theme.spacings.large};

  :hover .poop {
    visibility: visible;
  }
`

const ProductImage = styled.img`
  width: 35px;
  height: 35px;
  object-fit: cover;
`

const ShowMoreSection = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`

const DataProgressSections = styled.div`
  width: 80%;
  min-height: 45px;

  :hover .dataprogresssection {
    display: none;
  }

  :hover .adddata {
    display: block;
  }

  ${MediaQuery.MD} {
    width: 90%;
  }
`

const AddMoreDataButton = styled.div`
  display: none;
`

const IconButton = styled.div`
  padding-left: 0.5rem;
  cursor: pointer;
  border-radius: 50%;
  visibility: hidden;

  :hover {
    filter: brightness(80%);
  }
`

const TerminalRow = styled(Row)`
  cursor: pointer;
`

const TerminalOverviewTerminalBar: React.FunctionComponent<Props> = ({
  terminal,
  useGreyBackground,
  onClickTerminalDetails,
  cardOpen,
  selectMode,
  onSelectForExport,
  selectedForExport,
  onCloseCard,
}) => {
  const progressPercentage =
    (terminal.dataUsage && getPercentage(terminal.dataUsage.used, terminal.dataUsage.total)) ?? 0

  const [addDataOpen, setAddDataOpen] = React.useState(false)

  const onOpenTerminalStatistics = (terminalId: number) => {
    if (addDataOpen) {
      setAddDataOpen(false)
    }
    onClickTerminalDetails(terminalId)
  }

  const onClickOpenAddData = (terminalId: number) => {
    if (cardOpen) {
      onCloseCard(terminalId)
    }

    setAddDataOpen(!addDataOpen)
  }

  return (
    <BarContentContainer greybg={useGreyBackground}>
      <TerminalRow align="center" onClick={() => onOpenTerminalStatistics(+terminal.id)}>
        <Col md={2}>
          <Row wrap="nowrap">
            {selectMode && (
              <>
                <Spacer spacing={1} />
                <Checkbox
                  disabled={terminal.apiTerminalId === undefined}
                  onChange={(e) => {
                    e.stopPropagation()
                    onSelectForExport?.(!selectedForExport)
                  }}
                  checked={selectedForExport}
                />
                <Spacer spacing={1} />
              </>
            )}
            <div style={{ display: "flex", alignItems: "center" }}>
              <ProductImage src={terminal.imgSrc} />
              <Spacer spacing={0.5} />
              <BodyMedium>{terminal.name}</BodyMedium>
            </div>
          </Row>
        </Col>
        <Col md={2}>
          <BodyMedium textAlign="center">{terminal.serialNumber}</BodyMedium>
        </Col>
        <Col md={2}>
          <BodyMedium>{terminal.suborganization}</BodyMedium>
        </Col>
        <Col md={2}>
          <BodyMedium>{getTerminalGroupsText(terminal?.groups ?? [])}</BodyMedium>
        </Col>
        <Col md={2}>
          {terminal.apiTerminalId === undefined || terminal.apiPlanId === undefined ? (
            <TooltipContainer>
              <BodyMedium>No Dataplan</BodyMedium>
              <Tooltip
                backgroundColor={theme.colors.greyDark}
                borderRadius="12px"
                tooltipPosition={noDataPlanTooltipPosition}
                unClickable={true}
                toolTipPadding="1rem"
              >
                <TooltipItem maxWidth="352px" noHover={true}>
                  <Body color="white">
                    If there already is a dataplan assigned to this terminal, contact web@satcube.com or your Satcube
                    representative to make it visible here.
                  </Body>
                </TooltipItem>
              </Tooltip>
            </TooltipContainer>
          ) : (
            <div style={{ display: "flex", alignItems: "center" }}>
              <TerminalStatusCircle status={terminal?.status} />
              <Spacer spacing={0.5} />
              <BodyMedium>{terminal?.status}</BodyMedium>
            </div>
          )}
        </Col>
        <Col md={2} style={{ paddingLeft: -15, paddingRight: -15, maxHeight: 50 }}>
          <ShowMoreSection>
            {terminal.apiTerminalId !== undefined && terminal.apiPlanId !== undefined ? (
              <DataProgressSections>
                <div className="dataprogresssection">
                  <Body textAlign="center">{terminal.dataplan}</Body>
                  <ProgressBar warningMax={TERMINAL_MAX_WARNING} currentProgress={progressPercentage} border />
                  <Body textAlign="center" style={{ whiteSpace: "nowrap" }}>
                    {mbGbDisplay(terminal.dataUsage?.used ?? 0)} / {mbGbDisplay(terminal.dataUsage?.total ?? 0)}
                  </Body>
                </div>
                <AddMoreDataButton className="adddata">
                  <AddDataButton
                    title="Add data"
                    onClick={() => onClickOpenAddData(+terminal.id)}
                    disabled={terminal?.status === "ended" || terminal.sharedPlan}
                    secondary
                  />
                </AddMoreDataButton>
              </DataProgressSections>
            ) : (
              <BodyMedium>Not Available</BodyMedium>
            )}

            <IconButton className="poop" onClick={() => onOpenTerminalStatistics(+terminal.id)}>
              {cardOpen ? addDataOpen ? <ArrowDropDownIcon /> : <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </IconButton>
          </ShowMoreSection>
        </Col>
      </TerminalRow>
      {!addDataOpen && cardOpen && (
        <UsageStatisticsSection
          isOpen={cardOpen}
          id={terminal.apiTerminalId}
          name={terminal.name}
          detailsData={terminal}
        />
      )}
      {terminal.apiPlanId !== undefined && (
        <AddDataSection
          isOpen={addDataOpen}
          onClickCancel={() => onClickOpenAddData(+terminal.id)}
          apiPlanId={terminal.apiPlanId}
          dataPlanName={terminal.dataplan}
        />
      )}
    </BarContentContainer>
  )
}

export default TerminalOverviewTerminalBar
